import { useState, useRef } from 'react'
import { useProjects } from '../../hooks/useProjects'
import { Modal } from '../Common/Modal'
import { Button } from '../Common/Button'
import { useTechnicalCard } from '../../hooks/useTechnicalCard'
import { Upload, X, FileText } from 'lucide-react'
import toast from 'react-hot-toast'

export const CreateProjectModal = ({ isOpen, onClose, onSuccess }) => {
  const { uploadTechnicalCard, loading } = useProjects()
  const [selectedFile, setSelectedFile] = useState(null)
  const [projectName, setProjectName] = useState('')
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef(null)

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Валидация типа файла
    if (file.type !== 'application/pdf') {
      toast.error('Пожалуйста, выберите PDF файл')
      return
    }

    // Валидация размера файла (максимум 50MB)
    const maxSize = 50 * 1024 * 1024
    if (file.size > maxSize) {
      toast.error('Размер файла не должен превышать 50MB')
      return
    }

    setSelectedFile(file)
    setUploadProgress(0)

    // Автоподстановка имени проекта из имени файла (без расширения),
    // если пользователь ещё не ввёл своё
    if (!projectName.trim()) {
      setProjectName(file.name.replace(/\.pdf$/i, ''))
    }
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDragDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()

    const file = event.dataTransfer.files?.[0]
    if (!file) return

    if (file.type !== 'application/pdf') {
      toast.error('Пожалуйста, перетащите PDF файл')
      return
    }

    setSelectedFile(file)
    setUploadProgress(0)
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Пожалуйста, выберите файл')
      return
    }

    // Имя проекта - обязательное поле
    if (!projectName.trim()) {
      toast.error('Пожалуйста, введите имя проекта')
      return
    }

    try {
      // Имитация прогресса загрузки
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval)
            return prev
          }
          return prev + Math.random() * 30
        })
      }, 500)

      const response = await uploadTechnicalCard(selectedFile, projectName.trim())

      clearInterval(progressInterval)
      setUploadProgress(100)

      toast.success(`Проект «${projectName.trim()}» успешно создан`)

      // Очищаем форму
      setSelectedFile(null)
      setProjectName('')
      setUploadProgress(0)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      // Вызываем callback успеха
      onSuccess?.(response)
      onClose()
    } catch (error) {
      setUploadProgress(0)
      toast.error(error.message || 'Ошибка при загрузке файла')
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setUploadProgress(0)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Создать новый проект">
      <div className="space-y-6">
        {/* Имя проекта (обязательное поле) */}
        <div>
          <label htmlFor="project-name" className="block text-sm font-medium text-gray-700 mb-1">
            Имя проекта <span className="text-red-500">*</span>
          </label>
          <input
            id="project-name"
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="Например: Площадка обслуживания ТС-1.03"
            maxLength={255}
            disabled={loading}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
              projectName.trim() ? 'border-gray-300' : 'border-red-300'
            }`}
          />
          {!projectName.trim() && (
            <p className="text-xs text-red-500 mt-1">Обязательное поле</p>
          )}
        </div>

        {/* Зона загрузки файла */}
        <div
          onDragOver={handleDragOver}
          onDrop={handleDragDrop}
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            selectedFile
              ? 'border-green-400 bg-green-50'
              : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            className="hidden"
            disabled={loading}
          />

          {selectedFile ? (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-3">
                <FileText className="text-green-600" size={32} />
                <div className="text-left">
                  <p className="font-semibold text-gray-900">
                    {selectedFile.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                onClick={handleRemoveFile}
                disabled={loading}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
              >
                Выбрать другой файл
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <Upload className="mx-auto text-gray-400" size={40} />
              <div>
                <p className="font-semibold text-gray-900 mb-1">
                  Перетащите PDF файл сюда
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  или нажмите для выбора файла
                </p>
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="text-blue-600 hover:text-blue-700 font-medium text-sm disabled:opacity-50"
              >
                Выбрать файл
              </button>
            </div>
          )}
        </div>

        {/* Информация о требованиях */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            <span className="font-semibold">Требования к файлу:</span>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Формат: PDF</li>
              <li>Максимальный размер: 50 MB</li>
              <li>Содержит техническую карту с описанием операций</li>
            </ul>
          </p>
        </div>

        {/* Прогресс загрузки */}
        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Загрузка...</span>
              <span className="font-semibold text-gray-900">
                {Math.round(uploadProgress)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-600 h-full rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Кнопки действия */}
        <div className="flex gap-2 justify-end pt-4 border-t">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Отмена
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            loading={loading}
            disabled={!selectedFile || !projectName.trim() || loading}
          >
            {loading ? 'Загрузка...' : 'Загрузить'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
